// @ts-strict-ignore
import {
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ComponentProps, CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BankAccount,
  Box,
  Card,
  Grid,
  IconName,
  Spinner,
  Table,
  Text,
  useSnackBar,
} from '@usealloy/component-library';

import { EVIDENCE_DATA_TYPES } from '~/constants';
import { UserContext } from '~/dashboard/context';
import { useEventTypeEvaluationsContext } from '~/entities/evaluations/EventTypeEvaluations/context';
import { TransactionHistoryTable } from '~/entities/evaluations/transaction/history/TransactionHistoryTable/TransactionHistoryTable';
import apiCalls from '~/helpers/apiCalls';
import { useEvidencePermissions } from '~/helpers/hooks';
import { routes } from '~/helpers/routes';
import { createCopyToClipboardHandler } from '~/helpers/utils';
import { ACCOUNT_TABLE_COLUMNS } from '~/investigations/components/BankAccountStatusUpdateModal/constants';
import { selectInvestigationDetail } from '~/investigations/redux/selectors';
import { useAppSelector } from '~/store/reduxHooks';
import { EvidenceModal } from '~/views/EvidenceModal';


import { useLegacyTableData } from '../../../table/hooks';
import { BankAccountStatusBadge } from '../BankAccountStatusUpdateModal/BankAccountStatusBadge';
import AccountStatusUpdateModal from '../BankAccountStatusUpdateModal/BankAccountStatusUpdateModal';
import type { BankAccount as BankAccountType } from '../BankAccountStatusUpdateModal/types';
import type { BankAccountData } from '../InvestigationTimeline/types';

type EllipsisMenuOption = NonNullable<ComponentProps<typeof BankAccount>['menuOptions']>[number];

const fullWidthSeparatorStyle: CSSProperties = {
  margin: 0,
  maxWidth: '100%',
  width: '100%',
  backgroundColor: 'var(--cl-gray-300)',
  padding: 0,
};

const bankAccountCountBadgeStyle: CSSProperties = {
  fontSize: 'var(--cl-font-size-2)',
  fontWeight: 'normal',
  backgroundColor: 'var(--cl-gray-500)',
  padding: 'var(--cl-space-1) var(--cl-space-2)',
  borderRadius: '100%',
  marginLeft: 'var(--cl-space-2)',
  verticalAlign: 'text-top',
};

interface InvestigationTrxBaTabProps {
  bordered?: boolean;
}

export const InvestigationTrxBaTab = ({
  bordered = false,
}: InvestigationTrxBaTabProps) => {
  const investigation = useAppSelector(selectInvestigationDetail);
  const [bankAccountsIsCollapsed, setBankAccountsIsCollapsed] = useState(false);
  const [showAccountStatusUpdateModal, setShowAccountStatusUpdateModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [currentSelectAccount, setCurrentSelectAccount] = useState<BankAccountType | null>(null);
  const addSnackBar = useSnackBar();
  const {
    permissions,
    typhonDomain,
  } = useContext(UserContext);

  // In the evaluation (`bordered`) context this card is scoped to the alert under
  // review, so transactions must reflect that alert's entity rather than the
  // investigation's primary entity. Otherwise multi-entity / group alerts show the
  // wrong entity's transactions (or none). Outside the evaluation context this
  // resolves to an empty entity, which is fine because bordered mode is only ever
  // rendered inside the evaluation provider.
  const { entity: alertEntity } = useEventTypeEvaluationsContext();

  const {
    data: rawAccountsAndTransactionsData,
    // react-query v4 keeps `isLoading` truthy for disabled queries; `isInitialLoading`
    // is only true while an enabled query is actively fetching, so a guarded query
    // with a missing investigation token resolves to an empty state (not a stuck spinner).
    isInitialLoading: isLoading,
  } = useQuery({
    enabled: Boolean(investigation?.investigationToken),
    queryKey: ['accounts-and-transactions', investigation.investigationToken],
    queryFn: async () => {
      const response = await apiCalls.investigations.getInvestigationAccountsAndTransactions({
        investigationToken: investigation.investigationToken,
        typhonDomain,
      });

      return response;
    },
  });

  const bankAccountsData = rawAccountsAndTransactionsData?.bank_accounts ?? [];
  const transactions = rawAccountsAndTransactionsData?.transactions ?? [];

  // Check evidence permissions (includes feature flag check)
  const { canReadEvidence, evidenceButtonText } = useEvidencePermissions();

  const cardStyle = useMemo(() => ({
    borderRadius: 0,
    marginBottom: 'var(--cl-space-4)',
    ...(bankAccountsIsCollapsed && {
      borderBottom: '1px solid var(--cl-gray-300)',
    }),
  }), [bankAccountsIsCollapsed]);

  const [investigationEntity] = Object.values(investigation.entities || {});
  const entityToken = bordered
    ? alertEntity?.entity_token
    : investigationEntity?.entityToken;

  const isGroupInvestigation = Boolean(investigation?.isGroupInvestigation);

  const copyToClipboard = createCopyToClipboardHandler(addSnackBar);

  const listWebhooksUrl = window.location.origin + routes.settings.webhooks.list;
  const {
    data: {
      data: webhookData,
    },
    isFetching: isWebhookDataLoading,
  } = useLegacyTableData<any>(['webhook-settings-view'], {
    url: listWebhooksUrl,
    enabled: true,
    enableJson: true,
  });

  const isWebhookPermitted = (permissionActionKey: string): boolean => !isWebhookDataLoading
    && Array.isArray(webhookData)
    && webhookData.some(webhook => webhook.active === true
      && Array.isArray(webhook.permission_actions)
      && webhook.permission_actions.some(pa => pa.permission_action_key === permissionActionKey,
      )
      && !investigation.investigationArchivedAt
      && permissions['UPDATE:Investigations:STATUS_WEBHOOKS'],
    );

  const hasInvTransactionsWebhook = isWebhookPermitted('UPDATE:Transactions:status.investigations');
  const hasInvBankAccountsWebhook = isWebhookPermitted('UPDATE:BankAccountSnapshots:status_description.investigations');

  const {
    webhookTrxEvents: webhookAuditEventDetailsByTransactionId,
    webhookBankAccountEvents: webhookAuditEventDetailsByBankAccountToken,
  } = useMemo(() => (
    Object.values(investigation?.webhookAuditEventDetails || {}).reduce(
      (acc, webhookAuditEvent) => {
        if (webhookAuditEvent?.transactions?.length > 0) {
          webhookAuditEvent.transactions.forEach(
            ({ external_transaction_identifier }) => {
              if (
                !acc.webhookTrxEvents[external_transaction_identifier]
                || (
                  acc
                    .webhookTrxEvents[external_transaction_identifier]
                    .timestamp < webhookAuditEvent.timestamp
                )
              ) {
                acc.webhookTrxEvents[external_transaction_identifier] = webhookAuditEvent;
              }
            },
          );
        } else if (webhookAuditEvent.bank_accounts.length > 0) {
          webhookAuditEvent.bank_accounts.forEach(({ bank_account_token }) => {
            if (
              !acc.webhookBankAccountEvents[bank_account_token]
              || (
                acc
                  .webhookBankAccountEvents[bank_account_token]
                  .timestamp < webhookAuditEvent.timestamp
              )
            ) {
              acc.webhookBankAccountEvents[bank_account_token] = webhookAuditEvent;
            }
          });
        }
        return acc;
      },
      { webhookTrxEvents: {}, webhookBankAccountEvents: {} },
    )
  ), [investigation?.webhookAuditEventDetails]);

  const bankAccountsExternalState = {
    isCollapsed: bankAccountsIsCollapsed,
    setIsCollapsed: setBankAccountsIsCollapsed,
  };

  // In the evaluation context (`bordered`) reuse the standard evaluation-card
  // config — plain string header, `collapsible`, and the Card's default padding,
  // radius, and header spacing — so these match the other cards. In the
  // investigation tab keep the full-width, borderless treatment.
  const transactionsCardProps = bordered
    ? { header: 'Transactions', collapsible: true }
    : {
      borderless: true,
      compact: true,
      header: (
        <Text as="h3" data-pendo-anchor="inv-trx-table" size={4} weight={500}>
          Transactions
        </Text>
      ),
      separatorStyle: fullWidthSeparatorStyle,
    };

  const bankAccountsCardProps = bordered
    ? {
      header: `Bank Accounts (${bankAccountsData.length})`,
      collapsible: true,
      externalControlledState: bankAccountsExternalState,
    }
    : {
      borderless: true,
      compact: true,
      collapsible: true,
      header: (
        <div style={{ width: '100%' }} data-pendo-anchor="inv-bank-accounts-section">
          <Text as="h3" size={4} weight={500}>
            Bank Accounts
            <span style={bankAccountCountBadgeStyle}>
              {bankAccountsData.length}
            </span>
          </Text>
        </div>
      ),
      separatorStyle: fullWidthSeparatorStyle,
      externalControlledState: bankAccountsExternalState,
      style: cardStyle,
    };

  return (
    <>
      <Box
        direction="column"
        gap={bordered ? 4 : 1}
        style={{ padding: bordered ? 0 : '0 var(--cl-space-6) 0' }}
      >
        <Card {...transactionsCardProps}>
          <TransactionHistoryTable
            entityToken={entityToken}
            hasEvidenceModal
            hasInvTransactionsWebhook={hasInvTransactionsWebhook}
            investigationToken={investigation?.investigationToken}
            isGroupInvestigation={isGroupInvestigation}
            isInvestigationPage
            // In bordered/evaluation mode, let the table fetch the reviewed alert's
            // transactions itself (keyed on `entityToken`), matching the pre-#55929
            // behavior. The full-width investigation tab keeps passing the
            // investigation-level accounts/transactions data.
            isLoading={bordered ? undefined : isLoading}
            transactions={bordered ? undefined : transactions}
            webhookAuditEventDetailsByTransactionId={webhookAuditEventDetailsByTransactionId}
          />
        </Card>
        <Card {...bankAccountsCardProps}>
          {isLoading ? (
            <Box gap={4}>
              <Spinner />
              {' Loading bank accounts...'}
            </Box>
          ) : (
            <Grid
              colGap={5}
              style={{
                padding: 0,
              }}
            >
              {(
                bankAccountsData as BankAccountData[]
              ) && bankAccountsData.map(account => {
                const {
                  bank_account_token: bankAccountToken,
                  bank_account_id: bankAccountId,
                  account_number: accountNumber,
                  account_type: accountType,
                  card_network: cardNetwork,
                  account_class: accountClass,
                  country_code: countryCode,
                  currency,
                  activation_date: activationDate,
                  is_linked_account: isLinkedAccount,
                  closing_date: closingDate,
                  opening_date: openingDate,
                  account_name: accountName,
                  routing_number: routingNumber,
                  entities,
                  account_type_status: accountTypeStatus,
                  evidence_count: evidenceCount,
                  external_account_identifier: externalAccountIdentifier,
                } = account?.bank_account || {};

                const webhookAuditEventDetails = webhookAuditEventDetailsByBankAccountToken[
                  bankAccountToken
                ];

                const menuOptions: EllipsisMenuOption[] = [
                  ...(hasInvBankAccountsWebhook ? [{
                    name: 'Change Status',
                    onClick: () => {
                      setCurrentSelectAccount(account);
                      setShowAccountStatusUpdateModal(true);
                    },
                  }] : []),
                  ...(!investigation.investigationArchivedAt
                    && canReadEvidence ? [{
                      name: evidenceButtonText,
                      onClick: () => {
                        setCurrentSelectAccount(account);
                        setShowEvidenceModal(true);
                      },
                    }] : []),
                  ...(bankAccountToken ? [{
                    name: bankAccountToken,
                    helpText: 'Bank Account Token',
                    icon: IconName.Copy,
                    onClick: copyToClipboard({
                      text: bankAccountToken,
                      successMessage: 'Account token copied to clipboard',
                    }),
                  }] : []),
                  ...(externalAccountIdentifier ? [{
                    name: externalAccountIdentifier,
                    helpText: 'External Account ID',
                    icon: IconName.Copy,
                    onClick: copyToClipboard({
                      text: externalAccountIdentifier,
                      successMessage: 'External account token copied to clipboard',
                    }),
                  }] : []),
                ];

                return (
                  <Grid.Item
                    columns={{ sm: 12, md: 6, lg: 6 }}
                    key={bankAccountId}
                  >
                    <Box
                      style={{
                        width: '100%',
                        marginBottom: 'var(--cl-space-1)',
                        marginTop: 'var(--cl-space-3)',
                      }}
                    >
                      <BankAccount
                        accountNumber={accountNumber}
                        accountType={accountType}
                        // already in minor units — Typhon converts it, so don't scale again
                        balance={Number(account?.current_snapshot?.account_balance ?? 0)}
                        cardNetwork={cardNetwork}
                        class={accountClass}
                        countryCode={countryCode}
                        currencyCode={currency}
                        dateActivated={activationDate}
                        isExternalAccount={isLinkedAccount}
                        dateClosed={closingDate}
                        dateOpened={openingDate}
                        name={accountName}
                        numTransactions={account?.transaction_count}
                        routingNumber={routingNumber}
                        entities={entities}
                        size="large"
                        status={accountTypeStatus}
                        {...(canReadEvidence && {
                          evidenceCount,
                          onEvidenceClick: () => {
                            setCurrentSelectAccount(account);
                            setShowEvidenceModal(true);
                          },
                        })}
                        customStatusBadge={webhookAuditEventDetails?.value_to ? (
                          <BankAccountStatusBadge
                            status={accountTypeStatus}
                            pendingStatus={webhookAuditEventDetails?.value_to}
                          />
                        ) : undefined}
                        menuOptions={menuOptions}
                        pendoAnchor="account-evidence-notes-docs"
                      />
                    </Box>
                  </Grid.Item>
                );
              })}
            </Grid>
          )}
        </Card>
      </Box>
      <AccountStatusUpdateModal
        isOpen={showAccountStatusUpdateModal}
        closeModal={() => setShowAccountStatusUpdateModal(false)}
        bankAccount={currentSelectAccount}
        investigationToken={investigation?.investigationToken}
      />
      {canReadEvidence && (
        <EvidenceModal
          isOpen={showEvidenceModal}
          closeModal={() => setShowEvidenceModal(false)}
          sourceTokens={currentSelectAccount?.bank_account?.bank_account_token
            ? [currentSelectAccount.bank_account.bank_account_token]
            : []}
          sourceType={EVIDENCE_DATA_TYPES.BANK_ACCOUNT}
          contextToken={investigation?.investigationToken}
          sourceComponent={(
            <Table
              columns={ACCOUNT_TABLE_COLUMNS}
              data={currentSelectAccount ? [currentSelectAccount] : []}
            />
          )}
        />
      )}
    </>
  );
};
